/**
 * Daily Health logic. PURE — no React.
 *
 * Two storage shapes, and getting them the wrong way round is data loss:
 *   my-hub:health:fields   PERSISTENT  the user's field definitions
 *   my-hub:health:checked  DAILY       today's ticked ids, reset at midnight
 */
import { asRecord, asTrimmedString, type HealthField } from './types';

/** Spec: "Default suggestions: water intake, protein goal, movement/workout". */
export const DEFAULT_HEALTH_FIELDS: readonly HealthField[] = [
  { id: 'health-default-water', label: 'Water intake' },
  { id: 'health-default-protein', label: 'Protein goal' },
  { id: 'health-default-movement', label: 'Movement / workout' },
];

/**
 * Health is the one list panel with NO internal scroll region (only Todos and
 * Media may scroll), so the field count is capped rather than allowed to push
 * the card past the viewport. Rows go dense past DENSE_AFTER.
 */
export const MAX_HEALTH_FIELDS = 8;
export const DENSE_AFTER = 5;
export const MAX_FIELD_LABEL = 40;

export function canAddHealthField(fields: readonly HealthField[]): boolean {
  return fields.length < MAX_HEALTH_FIELDS;
}

/* ------------------------------------------------------------------ */
/* Derived state                                                       */
/* ------------------------------------------------------------------ */

export interface HealthCompletion {
  readonly done: number;
  readonly total: number;
  /** 0..1. Zero fields is 0, never NaN (0/0). */
  readonly ratio: number;
  /** 0..100, rounded — the progress bar width. */
  readonly percent: number;
}

/**
 * Only ids that still correspond to a live field count. A deleted field's
 * stale tick can linger in today's `checked` array without inflating the
 * ratio past 100%.
 */
export function healthCompletion(
  fields: readonly HealthField[],
  checkedIds: readonly string[],
): HealthCompletion {
  const set = new Set(checkedIds);
  const total = fields.length;
  const done = fields.reduce((n, f) => (set.has(f.id) ? n + 1 : n), 0);
  const ratio = total === 0 ? 0 : done / total;
  return { done, total, ratio, percent: Math.round(ratio * 100) };
}

export function toggleChecked(checkedIds: readonly string[], id: string): readonly string[] {
  return checkedIds.includes(id) ? checkedIds.filter((x) => x !== id) : [...checkedIds, id];
}

/** Drops ticks for fields that no longer exist. Used after a field delete. */
export function pruneChecked(
  checkedIds: readonly string[],
  fields: readonly HealthField[],
): readonly string[] {
  const live = new Set(fields.map((f) => f.id));
  return checkedIds.filter((id) => live.has(id));
}

/* ------------------------------------------------------------------ */
/* Persistence guards                                                  */
/* ------------------------------------------------------------------ */

/**
 * Salvaging migration for `my-hub:health:fields`. PURE. Non-array -> [],
 * which the panel renders as its "add your first field" empty state rather
 * than silently resurrecting the defaults over the user's deletions.
 */
export function migrateHealthFields(raw: unknown): readonly HealthField[] {
  if (!Array.isArray(raw)) return [];
  const out: HealthField[] = [];
  const seen = new Set<string>();
  raw.forEach((row, index) => {
    const r = asRecord(row);
    if (r === null) return;
    const label = asTrimmedString(r.label).slice(0, MAX_FIELD_LABEL);
    if (label === '') return;
    const id = asTrimmedString(r.id) || `health-legacy-${index}`;
    if (seen.has(id)) return;
    seen.add(id);
    out.push({ id, label });
  });
  return out.slice(0, MAX_HEALTH_FIELDS);
}

/** Salvaging migration for `my-hub:health:checked` — a de-duped id array. */
export function migrateHealthChecked(raw: unknown): readonly string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  for (const v of raw) {
    const id = asTrimmedString(v);
    if (id !== '') seen.add(id);
  }
  return [...seen];
}
